import { useEffect, useMemo, useState, type FormEvent } from "react";
import { api, ApiError } from "../lib/api.js";
import { clearOnEscape } from "../lib/clearOnEscape.js";
import { hashForRoute } from "../lib/hashRoute.js";
import type { Project } from "../lib/types.js";
import { useDebounced } from "../lib/useDebounced.js";

const inputCls =
  "w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 " +
  "outline-none focus:ring-2 focus:ring-(--accent) dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100";

const btnPrimary =
  "shrink-0 rounded-md bg-(--accent) px-4 py-2 text-sm font-medium text-(--accent-contrast) " +
  "hover:bg-(--accent-hover) disabled:opacity-50";

export function Projects() {
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState("");
  const debouncedFilter = useDebounced(filter);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [busy, setBusy] = useState(false);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const res = await api<{ items: Project[] }>("/api/projects");
      setProjects(res.items);
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) return;
      setError(err instanceof Error ? err.message : "Could not load projects.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  const visible = useMemo(() => {
    const q = debouncedFilter.trim().toLowerCase();
    if (!q) return projects;
    return projects.filter((p) => p.name.toLowerCase().includes(q) || p.description.toLowerCase().includes(q));
  }, [projects, debouncedFilter]);

  async function handleCreate(e: FormEvent) {
    e.preventDefault();
    if (!newName.trim() || busy) return;
    setBusy(true);
    setError(null);
    try {
      const project = await api<Project>("/api/projects", { method: "POST", json: { name: newName.trim() } });
      setProjects((prev) => [project, ...prev]);
      setNewName("");
      setCreating(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not create the project.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mx-auto max-w-3xl p-6">
      <div className="flex items-center justify-between gap-3">
        <h1 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Projects</h1>
        <button
          type="button"
          className={btnPrimary}
          onClick={() => {
            setCreating((v) => !v);
            setNewName("");
          }}
        >
          {creating ? "Cancel" : "New project"}
        </button>
      </div>

      {creating && (
        <form onSubmit={(e) => void handleCreate(e)} className="mt-4 flex gap-2">
          <input
            autoFocus
            className={inputCls}
            placeholder="Project name"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
          />
          <button type="submit" disabled={busy || !newName.trim()} className={btnPrimary}>
            {busy ? "Creating..." : "Create"}
          </button>
        </form>
      )}

      <div className="relative mt-4">
        <input
          className={inputCls}
          placeholder="Search projects..."
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          onKeyDown={clearOnEscape(filter, setFilter)}
        />
        {filter && (
          <button
            type="button"
            aria-label="Clear search"
            onClick={() => setFilter("")}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
          >
            &times;
          </button>
        )}
      </div>

      {error && <p className="mt-3 text-sm text-red-600 dark:text-red-400">{error}</p>}
      {loading && <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">Loading...</p>}

      {!loading && (
        <ul className="mt-4 divide-y divide-gray-200 rounded-md border border-gray-200 bg-white dark:divide-gray-800 dark:border-gray-800 dark:bg-gray-900">
          {visible.map((p) => (
            <li key={p.projectId}>
              <a
                href={hashForRoute({ kind: "projectDetail", projectId: p.projectId })}
                className="flex items-center justify-between gap-3 px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-800"
              >
                <div className="min-w-0">
                  <div className="truncate font-medium text-gray-900 dark:text-gray-100">{p.name}</div>
                  {p.description && (
                    <div className="truncate text-sm text-gray-500 dark:text-gray-400">{p.description}</div>
                  )}
                </div>
                <span className="shrink-0 text-sm text-gray-400">&rarr;</span>
              </a>
            </li>
          ))}
          {visible.length === 0 && (
            <li className="px-4 py-6 text-center text-sm text-gray-500 dark:text-gray-400">No projects match.</li>
          )}
        </ul>
      )}
    </div>
  );
}
